import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Camera, Download, Heart, MessageSquare, Check, CheckCheck, Share2, ArrowLeft, Send } from 'lucide-react';
import PhotoLightbox from '../components/PhotoLightbox';
import BrandLogo from '../components/BrandLogo';
import { clientApi } from '../api';
import { useBranding } from '../context/BrandingContext';
import { toast } from 'sonner';
import JSZip from 'jszip';

const GalleryView = () => {
  const { clientSlug, gallerySlug, subgallerySlug } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null); // { gallery, subgallery, photos }
  const [selected, setSelected] = useState(new Set());
  const [openIndex, setOpenIndex] = useState(-1);
  const [filter, setFilter] = useState('all');
  const [sending, setSending] = useState(false);
  const [note, setNote] = useState('');
  const [showSendDialog, setShowSendDialog] = useState(false);
  const displayName = clientApi.displayName(clientSlug);
  const { branding } = useBranding();
  const wm = branding?.watermark;

  const reload = async () => {
    try {
      const d = await clientApi.getSubgallery(clientSlug, gallerySlug, subgallerySlug);
      setData(d);
    } catch (err) {
      toast.error('Sotto-galleria non trovata'); nav(`/c/${clientSlug}/lavori/${gallerySlug}`);
    }
  };

  useEffect(() => {
    if (!clientApi.hasToken(clientSlug)) { nav(`/c/${clientSlug}`, { replace: true }); return; }
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSlug, gallerySlug, subgallerySlug]);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (filter === 'liked') return data.photos.filter(p => (p.likes_by || []).includes(displayName));
    if (filter === 'selected') return data.photos.filter(p => selected.has(p.id));
    return data.photos;
  }, [data, filter, selected, displayName]);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const allSelected = filtered.length > 0 && filtered.every(p => selected.has(p.id));
  const toggleSelectAll = () => {
    setSelected(prev => {
      const n = new Set(prev);
      if (allSelected) { filtered.forEach(p => n.delete(p.id)); }
      else { filtered.forEach(p => n.add(p.id)); }
      return n;
    });
    toast.success(allSelected ? 'Selezione annullata' : `${filtered.length} foto selezionate`);
  };

  const toggleLike = async (photoId) => {
    try { await clientApi.toggleLike(clientSlug, photoId); await reload(); }
    catch (err) { toast.error('Impossibile aggiornare like'); }
  };

  const addComment = async (photoId, text) => {
    if (!text.trim()) return;
    try { await clientApi.addComment(clientSlug, photoId, text.trim()); await reload(); }
    catch (err) { toast.error('Impossibile inviare commento'); }
  };

  const downloadOne = async (url, filename) => {
    try {
      const res = await fetch(url); const blob = await res.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = filename || 'foto.jpg';
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('Download avviato');
    } catch { toast.error('Impossibile scaricare'); }
  };

  const [zipping, setZipping] = useState(false);

  const downloadSelected = async () => {
    if (selected.size === 0) return toast.error('Nessuna foto selezionata');
    const photosToZip = data.photos.filter(p => selected.has(p.id));
    setZipping(true);
    toast.success(`Preparo lo ZIP di ${photosToZip.length} foto...`);
    try {
      const zip = new JSZip();
      const used = new Set();
      for (const p of photosToZip) {
        const res = await fetch(p.url);
        const blob = await res.blob();
        let name = p.filename || `${p.id}.jpg`;
        if (used.has(name)) name = `${p.id}_${name}`;
        used.add(name);
        zip.file(name, blob);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(content);
      a.download = `${((data.subgallery.title || 'foto')).replace(/[^\w\s-]/g, '').trim() || 'foto'}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('ZIP pronto, download avviato!');
    } catch { toast.error('Errore durante la creazione dello ZIP'); }
    finally { setZipping(false); }
  };

  const sendSelection = async () => {
    if (selected.size === 0) return toast.error('Seleziona almeno una foto');
    setSending(true);
    try {
      await clientApi.submitSelection(clientSlug, {
        subgalleryId: data.subgallery.id,
        photoIds: Array.from(selected),
        note: note.trim(),
      });
      toast.success(`Selezione di ${selected.size} foto inviata a Nicola!`);
      setShowSendDialog(false);
      setNote('');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Errore invio'); }
    finally { setSending(false); }
  };

  if (!data) return null;
  const { gallery, subgallery, photos } = data;
  const likedCount = photos.filter(p => (p.likes_by || []).includes(displayName)).length;

  return (
    <div className="min-h-screen bg-[#0B0B0D] text-white">
      <header className="sticky top-0 z-30 bg-black/70 backdrop-blur border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <Link to={`/c/${clientSlug}/lavori/${gallerySlug}`} className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-white shrink-0">
            <ArrowLeft className="w-4 h-4" /> {gallery.title}
          </Link>
          <div className="h-4 w-px bg-white/10" />
          <BrandLogo size="sm" hideText />
          <div className="min-w-0 ml-2">
            <p className="text-[10px] uppercase tracking-widest text-[#C9A961]">{gallery.title} · Sotto-galleria</p>
            <h1 className="font-display text-lg md:text-xl truncate">{subgallery.title}</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={()=>setShowSendDialog(true)} disabled={selected.size===0} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition ${selected.size===0 ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-white/10 hover:bg-white/20 border border-[#C9A961]/40 text-[#C9A961]'}`}>
              <Send className="w-3.5 h-3.5" /> Invia selezione
            </button>
            <button data-testid="download-zip-btn" onClick={downloadSelected} disabled={selected.size===0 || zipping} className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-semibold transition ${selected.size===0 ? 'bg-white/5 text-slate-500 cursor-not-allowed' : 'bg-[#C9A961] hover:bg-white text-black'}`}>
              <Download className="w-3.5 h-3.5" /> {zipping ? 'Preparo ZIP...' : `Scarica ZIP (${selected.size})`}
            </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-2 flex items-center gap-2 text-xs">
          {[
            {k:'all', l:`Tutte (${photos.length})`},
            {k:'liked', l:`Preferite (${likedCount})`},
            {k:'selected', l:`Selezionate (${selected.size})`},
          ].map(t => (
            <button key={t.k} onClick={()=>setFilter(t.k)} className={`px-3 py-1.5 rounded-full font-medium transition ${filter===t.k ? 'bg-[#C9A961] text-black' : 'bg-white/5 text-slate-300 border border-white/10 hover:border-[#C9A961]/50'}`}>{t.l}</button>
          ))}
          <button data-testid="select-all-btn" onClick={toggleSelectAll} className={`ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full font-semibold transition ${allSelected ? 'bg-white/10 text-slate-200 border border-white/20 hover:bg-white/20' : 'bg-[#C9A961]/15 text-[#C9A961] border border-[#C9A961]/40 hover:bg-[#C9A961]/25'}`}>
            <CheckCheck className="w-3.5 h-3.5" /> {allSelected ? 'Deseleziona tutte' : `Seleziona tutte (${filtered.length})`}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-6">
        <div className="masonry">
          {filtered.map(p => {
            const liked = (p.likes_by || []).includes(displayName);
            const isSel = selected.has(p.id);
            return (
              <div key={p.id} className="masonry-item photo-card group relative rounded-xl overflow-hidden bg-white/5">
                <img src={p.url} alt={p.filename} className="w-full h-auto cursor-zoom-in" onClick={()=>setOpenIndex(photos.findIndex(x=>x.id===p.id))} loading="lazy" />
                {wm?.enabled && (
                  <div className="wm-overlay">
                    <span className="wm-text" style={{ opacity: wm.opacity ?? 0.15 }}>{wm.text || 'FRAMEBOX'}</span>
                  </div>
                )}
                {isSel && <div className="absolute inset-0 ring-4 ring-[#C9A961] rounded-xl pointer-events-none" />}
                <div className="absolute top-2 left-2">
                  <button onClick={()=>toggleSelect(p.id)} className={`w-7 h-7 rounded-full grid place-items-center transition ${isSel ? 'bg-[#C9A961] text-black' : 'bg-black/60 text-white/80 hover:bg-black/80'}`} title="Seleziona">
                    <Check className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute top-2 right-2 flex items-center gap-1.5">
                  <button onClick={()=>toggleLike(p.id)} className={`w-8 h-8 rounded-full grid place-items-center transition ${liked ? 'bg-rose-500 text-white' : 'bg-black/60 text-white/80 hover:text-rose-400'}`} title="Metti like">
                    <Heart className={`w-4 h-4 ${liked ? 'fill-current' : ''}`} />
                  </button>
                  <button onClick={()=>downloadOne(p.url, p.filename)} className="w-8 h-8 rounded-full grid place-items-center bg-black/60 text-white/80 hover:bg-black/80" title="Scarica">
                    <Download className="w-4 h-4" />
                  </button>
                </div>
                <div className="absolute bottom-0 inset-x-0 p-2 flex items-center justify-between text-white text-xs opacity-0 group-hover:opacity-100 transition bg-gradient-to-t from-black/70 to-transparent">
                  <div className="flex items-center gap-3">
                    <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5" /> {p.likes || 0}</span>
                    <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {(p.comments || []).length}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {filtered.length === 0 && <div className="text-center py-20 text-slate-500">Nessuna foto in questa vista.</div>}
      </main>

      {openIndex >= 0 && (
        <PhotoLightbox
          photos={photos}
          index={openIndex}
          onClose={()=>setOpenIndex(-1)}
          onIndex={setOpenIndex}
          onLike={toggleLike}
          onComment={addComment}
          onDownload={downloadOne}
          clientName={displayName}
        />
      )}

      {showSendDialog && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <div className="bg-[#141416] border border-white/10 rounded-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-2 mb-4">
              <Send className="w-5 h-5 text-[#C9A961]" />
              <h3 className="font-display text-2xl">Invia selezione a Nicola</h3>
            </div>
            <p className="text-sm text-slate-400 mb-4">Stai per inviare a Nicola una selezione di <b className="text-white">{selected.size} foto</b> dalla sotto-galleria "{subgallery.title}" del lavoro "{gallery.title}". Vuoi aggiungere una nota?</p>
            <textarea value={note} onChange={e=>setNote(e.target.value)} rows={3} placeholder="Es. Queste sono le mie foto preferite, mi piacerebbe averle stampate..." className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-[#C9A961] resize-none text-white placeholder:text-slate-500" />
            <div className="flex items-center justify-end gap-2 mt-5">
              <button onClick={()=>setShowSendDialog(false)} className="px-4 py-2 text-sm text-slate-400 hover:text-white">Annulla</button>
              <button onClick={sendSelection} disabled={sending} className="inline-flex items-center gap-2 bg-[#C9A961] hover:bg-white text-black text-sm font-semibold px-5 py-2.5 rounded-full transition">
                <Send className="w-3.5 h-3.5" /> {sending ? 'Invio...' : 'Invia selezione'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryView;
